import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useCoachDeckListMutation,
  useCounterDeckMutation,
  usePopularDecksQuery,
  useSaveDeckMutation,
} from '@/services/qbotApi';
import { Button } from '@/components/common/Button';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { SelectFilter } from '@/components/common/SelectFilter';
import { CoachPill, CounterPill, ImportPill, SavePill } from './DeckActions';
import { CoachOverlay } from './CoachOverlay';
import { AdvisorOverlay } from './AdvisorOverlay';
import { CounterResult } from './AdvisorResults';
import { ActionRow, DeckStage, StageHeader } from './DeckStage';
import { DeckStats } from './DeckStats';
import { saveDeckErrorKey } from './saveDeckError';

/**
 * The most decks `/decks/popular` can return in one call (server clamps to 50).
 * Taking it in one go lets the filters run locally: the archetype list is built
 * from decks that actually exist, filtering costs no round-trip, and a deck
 * keeps its true meta rank instead of being renumbered inside a filtered slice.
 */
const META_PAGE_SIZE = 50;

interface MetaDecksModeProps {
  isPro: boolean;
  /** Card names the list is filtered by — set by tapping your own cards. */
  cardFilter: string[];
  onRemoveCard: (name: string) => void;
  onClearCards: () => void;
}

/**
 * "Top Decks" — the meta, one deck per page, filling the panel without
 * scrolling. Same `<DeckStage>` frame as My Decks: paging swaps the deck +
 * stats in place and the coach opens as an overlay. The archetype/card filters
 * live in a pinned row above the stage and reset the pager to the first deck.
 */
export function MetaDecksMode({
  isPro,
  cardFilter,
  onRemoveCard,
  onClearCards,
}: MetaDecksModeProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [archetype, setArchetype] = useState('');
  const [rawPage, setRawPage] = useState(0);

  const { data, isLoading, error, refetch } = usePopularDecksQuery({ pageSize: META_PAGE_SIZE });
  const decks = useMemo(() => data?.decks ?? [], [data]);

  const archetypes = useMemo(
    () => [...new Set(decks.map((d) => d.archetype).filter(Boolean))].sort(),
    [decks],
  );

  // Mirrors the server's own filters: archetype equality and AND-across-cards.
  const filtered = useMemo(
    () =>
      decks
        .map((deck, i) => ({ deck, rank: i + 1 }))
        .filter(({ deck }) => !archetype || deck.archetype === archetype)
        .filter(({ deck }) =>
          cardFilter.every((name) =>
            deck.cards.some((c) => c.name.toLowerCase() === name.toLowerCase()),
          ),
        ),
    [decks, archetype, cardFilter],
  );

  const total = filtered.length;
  const page = total ? Math.min(rawPage, total - 1) : 0;

  // One deck on screen → coach / counter / save state is per-mode, reset on turn.
  const [coachOpen, setCoachOpen] = useState(false);
  const [analyse, listCoach] = useCoachDeckListMutation();
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [counterOpen, setCounterOpen] = useState(false);
  const [runCounter, counterState] = useCounterDeckMutation();

  const resetDeckState = () => {
    setCoachOpen(false);
    listCoach.reset();
    setSaved(false);
    setSaveError(null);
    setCounterOpen(false);
    counterState.reset();
  };
  const goTo = (p: number) => {
    resetDeckState();
    setRawPage(p);
  };
  const applyFilter = (fn: () => void) => {
    resetDeckState();
    setRawPage(0);
    fn();
  };

  if (isLoading) {
    return <MetaSkeleton />;
  }
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-rose-300">{t('popular.error')}</p>
        <Button variant="link" corpo="maiuscoletto" onClick={() => refetch()}>
          {t('panel.retry')}
        </Button>
      </div>
    );
  }

  const hasFilter = Boolean(archetype) || cardFilter.length > 0;

  const filterRow = (
    <div className="flex shrink-0 items-center gap-1.5">
      {/* Stesso componente dei filtri di Battaglie: due schermate della stessa
          sezione che filtrano in modo diverso si leggono come due app. */}
      <SelectFilter
        label={t('panel.meta.archetypeLabel', { defaultValue: 'Archetipo' })}
        value={archetype}
        onChange={(v) => applyFilter(() => setArchetype(v))}
        options={[
          { value: '', label: t('panel.meta.allArchetypes') },
          ...archetypes.map((a) => ({ value: a, label: a })),
        ]}
      />

      {cardFilter.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() => {
            haptics.selection();
            applyFilter(() => onRemoveCard(name));
          }}
          className="flex shrink-0 items-center gap-1 rounded-full bg-brand px-2 py-1 text-[10px] font-bold text-brand-fg"
        >
          <span className="max-w-[5rem] truncate">{name}</span>
          <X className="h-3 w-3" />
        </button>
      ))}
    </div>
  );

  if (total === 0) {
    return (
      <div className="flex h-full flex-col gap-2">
        {filterRow}
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          <p className="max-w-[16rem] text-xs text-fg-muted">{t('panel.meta.noMatch')}</p>
          {hasFilter && (
            <Button
              variant="link"
              corpo="maiuscoletto"
              onClick={() =>
                applyFilter(() => {
                  setArchetype('');
                  onClearCards();
                })
              }
            >
              {t('panel.meta.clearFilters')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const { deck, rank } = filtered[page];

  return (
    <div className="flex h-full flex-col gap-2">
      {filterRow}
      <div className="min-h-0 flex-1">
        <DeckStage
          page={page}
          total={total}
          onPrev={() => goTo(Math.max(0, page - 1))}
          onNext={() => goTo(Math.min(total - 1, page + 1))}
          header={
            <StageHeader name={`#${rank} · ${deck.archetype}`} page={page} total={total}>
              {typeof deck.winRate === 'number' && (
                <span className="shrink-0 text-[10px] tabular-nums text-fg-muted">
                  {t('panel.meta.wr', { value: metaWinRate(deck.winRate).toFixed(1) })}
                </span>
              )}
            </StageHeader>
          }
          grid={
            // Levels are meta placeholders here (collector reports them maxed).
            <DeckGrid cards={deck.cards} showLevels={false} avgElixir={deck.avgElixir} />
          }
          stats={
            <DeckStats archetype={deck.archetype} rating={deck.rating} avgElixir={deck.avgElixir} />
          }
          actions={
            <ActionRow error={saveError}>
              <ImportPill cards={deck.cards} label={t('panel.action.import')} />
              <SavePill
                saved={saved}
                saving={saving}
                onSave={async () => {
                  setSaveError(null);
                  try {
                    await saveDeck({
                      name: t('panel.meta.saveName', { archetype: deck.archetype, rank }),
                      cards: deck.cards.map((c) => c.name),
                    }).unwrap();
                    haptics.notification('success');
                    setSaved(true);
                  } catch (err) {
                    haptics.notification('error');
                    setSaveError(t(saveDeckErrorKey(err)));
                  }
                }}
                label={t('panel.action.save')}
                savedLabel={t('panel.action.saved')}
              />
              <CoachPill
                isPro={isPro}
                label={t('panel.action.coach')}
                onClick={() => {
                  setCoachOpen(true);
                  if (isPro && !listCoach.data && !listCoach.isLoading) {
                    analyse({ cards: deck.cards.map((c) => c.name) }).catch(() => undefined);
                  }
                }}
              />
              <CounterPill
                label={t('panel.action.counter')}
                onClick={() => {
                  setCounterOpen(true);
                  if (!counterState.data && !counterState.isLoading) {
                    runCounter({ opponentDeck: deck.cards.map((c) => c.name) }).catch(
                      () => undefined,
                    );
                  }
                }}
              />
            </ActionRow>
          }
          overlay={
            <>
              <CoachOverlay
                open={coachOpen}
                onClose={() => setCoachOpen(false)}
                isPro={isPro}
                loading={listCoach.isLoading}
                error={listCoach.error}
                analysis={listCoach.data?.analysis}
                usage={listCoach.data?.usage}
              />
              <AdvisorOverlay
                open={counterOpen}
                title={t('panel.counter.title')}
                onClose={() => setCounterOpen(false)}
                loading={counterState.isLoading}
                error={counterState.error}
                onRetry={() =>
                  runCounter({ opponentDeck: deck.cards.map((c) => c.name) }).catch(() => undefined)
                }
              >
                {counterState.data && <CounterResult result={counterState.data} />}
              </AdvisorOverlay>
            </>
          }
        />
      </div>
    </div>
  );
}

/**
 * `winRate` arrives as a 0-100 percentage on every path that sets it (the live
 * CR fallback omits it). The <= 1 branch guards the other scale in the same
 * codebase — qCrC's advisor payload uses the same field name for a 0-1 fraction.
 */
function metaWinRate(winRate: number): number {
  return winRate <= 1 ? winRate * 100 : winRate;
}

function MetaSkeleton() {
  return (
    <div className="flex h-full flex-col gap-2" aria-hidden>
      <div className="h-8 shrink-0 animate-pulse rounded-lg bg-bg-secondary" />
      <div className="grid grid-cols-4 gap-1.5">
        {Array.from({ length: 8 }, (_, j) => (
          <div key={j} className="aspect-square animate-pulse rounded-md bg-bg-secondary" />
        ))}
      </div>
      <div className="mt-1 flex flex-col gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-3 animate-pulse rounded bg-bg-secondary" />
        ))}
      </div>
    </div>
  );
}
